import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Page from '../../../componentes/plantilla/Page';
import Card from '../../../componentes/contenedores/Card';
import { PlanillaServicio } from '../../../servicios/planillaServicio';
import { mostraAlerta, mostraAlertaOk } from '../../../componentes/alerts/sweetAlert';

const GenerarPlanilla = () => {
  const navigate = useNavigate();

  // Fechas por defecto: Quincena actual
  const hoy = new Date();
  const year = hoy.getFullYear();
  const month = String(hoy.getMonth() + 1).padStart(2, '0');
  const diaFinMes = new Date(year, hoy.getMonth() + 1, 0).getDate();

  const fechaIniDefault = `${year}-${month}-16`;
  const fechaFinDefault = `${year}-${month}-${diaFinMes}`;

  const [tipoPeriodo, setTipoPeriodo] = useState('QUINCENAL');
  const [fechaInicio, setFechaInicio] = useState(fechaIniDefault);
  const [fechaFin, setFechaFin] = useState(fechaFinDefault);
  const [descripcion, setDescripcion] = useState(`Planilla 2da Quincena ${hoy.toLocaleString('es-HN', { month: 'long' })} ${year}`);
  const [observaciones, setObservaciones] = useState('');

  const [ventasEditadas, setVentasEditadas] = useState({});
  const [calculo, setCalculo] = useState(null);
  const [procesando, setProcesando] = useState(false);
  const [cargandoConfigs, setCargandoConfigs] = useState(true);

  const datosPage = {
    titulo: {
      titulo: 'Generar Período de Planilla',
      url: '/app/admin/planillas/generar',
      tituloUrl: 'Planillas',
      nombreUrl: 'Generar',
    },
  };

  const ejecutarCalculo = async (ventas = ventasEditadas, tipo = tipoPeriodo) => {
    try {
      const res = await PlanillaServicio.previsualizarCalculo({
        tipoPeriodo: tipo,
        fechaInicio,
        fechaFin,
        ventasPorEmpleado: ventas,
      });
      setCalculo(res);

      // Si aún no hemos inicializado las ventas editadas, las guardamos
      if (Object.keys(ventasEditadas).length === 0) {
        const initialVentas = {};
        res.detalles.forEach((d) => {
          initialVentas[d.empleadoId] = d.ventasPeriodo;
        });
        setVentasEditadas(initialVentas);
      }
    } catch (err) {
      console.error(err);
      mostraAlerta('Error al calcular previsualización de nómina', 'error');
    } finally {
      setCargandoConfigs(false);
    }
  };

  useEffect(() => {
    ejecutarCalculo();
  }, [tipoPeriodo, fechaInicio, fechaFin]);

  const handleCambioVenta = (empleadoId, nuevoValor) => {
    const parsed = parseFloat(nuevoValor) || 0;
    const nuevoMap = { ...ventasEditadas, [empleadoId]: parsed };
    setVentasEditadas(nuevoMap);
    ejecutarCalculo(nuevoMap, tipoPeriodo);
  };

  const handleGuardar = async (aprobarInmediato = false) => {
    if (!descripcion || !fechaInicio || !fechaFin) {
      mostraAlerta('Complete todos los campos del período', 'warning');
      return;
    }

    try {
      setProcesando(true);
      const res = await PlanillaServicio.generarPlanilla({
        descripcion,
        tipoPeriodo,
        fechaInicio,
        fechaFin,
        observaciones,
        ventasPorEmpleado: ventasEditadas,
      });

      if (aprobarInmediato) {
        await PlanillaServicio.aprobarPlanilla(res.periodo.id);
        mostraAlertaOk('Planilla generada y aprobada correctamente');
      } else {
        mostraAlertaOk('Planilla guardada exitosamente en estado Borrador');
      }

      navigate(`/app/admin/planillas/detalle/${res.periodo.id}`);
    } catch (err) {
      mostraAlerta('Error al procesar la planilla', 'error');
    } finally {
      setProcesando(false);
    }
  };

  const formatLempiras = (val) =>
    `L ${Number(val || 0).toLocaleString('es-HN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  return (
    <Page datos={datosPage}>
      {/* Botón Volver */}
      <div className="mb-3">
        <Link to="/app/admin/planillas" className="btn btn-outline-secondary btn-sm">
          <i className="fas fa-arrow-left mr-1"></i> Volver a Planillas
        </Link>
      </div>

      {/* Formulario de Configuración del Período */}
      <Card titulo="1. Parámetros del Período de Nómina">
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="font-weight-bold">Descripción del Período *</label>
            <input
              type="text"
              className="form-control"
              placeholder="Ej: Planilla 1ra Quincena Octubre 2026"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
            />
          </div>

          <div className="col-md-2 mb-3">
            <label className="font-weight-bold">Frecuencia *</label>
            <select
              className="form-control"
              value={tipoPeriodo}
              onChange={(e) => {
                const nuevoTipo = e.target.value;
                setTipoPeriodo(nuevoTipo);
                if (nuevoTipo === 'MENSUAL') {
                  setFechaInicio(`${year}-${month}-01`);
                  setFechaFin(`${year}-${month}-${diaFinMes}`);
                  setDescripcion(`Planilla Mensual ${hoy.toLocaleString('es-HN', { month: 'long' })} ${year}`);
                } else {
                  setFechaInicio(fechaIniDefault);
                  setFechaFin(fechaFinDefault);
                  setDescripcion(`Planilla 2da Quincena ${hoy.toLocaleString('es-HN', { month: 'long' })} ${year}`);
                }
              }}
            >
              <option value="QUINCENAL">Quincenal</option>
              <option value="MENSUAL">Mensual</option>
            </select>
          </div>

          <div className="col-md-2 mb-3">
            <label className="font-weight-bold">Fecha Inicio *</label>
            <input
              type="date"
              className="form-control"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
            />
          </div>

          <div className="col-md-2 mb-3">
            <label className="font-weight-bold">Fecha Fin *</label>
            <input
              type="date"
              className="form-control"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
            />
          </div>

          <div className="col-12 mb-2">
            <label className="font-weight-bold">Notas u Observaciones Contables</label>
            <input
              type="text"
              className="form-control"
              placeholder="Observaciones internas para contabilidad (opcional)..."
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
            />
          </div>
        </div>
      </Card>

      {/* Resumen de Totales Calculados */}
      {calculo && (
        <div className="row mb-4">
          <div className="col-lg-3 col-sm-6 mb-3">
            <div className="p-3 border rounded shadow-sm">
              <span className="text-muted text-uppercase small font-weight-bold">Salario Base Total</span>
              <h4 className="font-weight-bold my-1">{formatLempiras(calculo.totalBruto)}</h4>
              <small className="text-muted">{tipoPeriodo === 'QUINCENAL' ? 'Cálculo al 50%' : '100% Mensual'}</small>
            </div>
          </div>

          <div className="col-lg-3 col-sm-6 mb-3">
            <div className="p-3 border rounded border-success shadow-sm">
              <span className="text-success text-uppercase small font-weight-bold">Comisiones por Ventas</span>
              <h4 className="text-success font-weight-bold my-1">+{formatLempiras(calculo.totalComisiones)}</h4>
              <small className="text-muted">Incentivo vendedores</small>
            </div>
          </div>

          <div className="col-lg-3 col-sm-6 mb-3">
            <div className="p-3 border rounded border-danger shadow-sm">
              <span className="text-danger text-uppercase small font-weight-bold">Total Retenciones</span>
              <h4 className="text-danger font-weight-bold my-1">-{formatLempiras(calculo.totalDeducciones)}</h4>
              <small className="text-muted">IHSS, RAP y Préstamos</small>
            </div>
          </div>

          <div className="col-lg-3 col-sm-6 mb-3">
            <div className="p-3 bg-primary text-white rounded shadow-sm">
              <span className="text-white-50 text-uppercase small font-weight-bold">Total Neto a Pagar</span>
              <h4 className="text-white font-weight-bold my-1">{formatLempiras(calculo.totalNeto)}</h4>
              <small className="text-white-50">{calculo.totalEmpleados} colaboradores</small>
            </div>
          </div>
        </div>
      )}

      {/* Tabla de Previsualización y Edición de Ventas */}
      <Card
        titulo="2. Cálculo en Vivo y Desglose por Colaborador"
        subtitulo="Puede ajustar las ventas del período para cada vendedor; el cálculo de comisiones y deducciones se actualizará automáticamente."
      >
        {cargandoConfigs ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="text-muted mt-2">Calculando nómina...</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-bordered table-hover align-middle mb-0" style={{ fontSize: '0.92rem' }}>
              <thead className="thead-dark">
                <tr>
                  <th>Colaborador</th>
                  <th>Modalidad</th>
                  <th className="text-right">Sueldo Base</th>
                  <th className="text-right" style={{ minWidth: '150px' }}>
                    Ventas Período (L)
                  </th>
                  <th className="text-center">% Com.</th>
                  <th className="text-right">Comisión</th>
                  <th className="text-right">IHSS</th>
                  <th className="text-right">RAP</th>
                  <th className="text-right">Préstamo</th>
                  <th className="text-right">Total Reten.</th>
                  <th className="text-right font-weight-bold">Neto a Recibir</th>
                </tr>
              </thead>
              <tbody>
                {calculo?.detalles.map((det) => (
                  <tr key={det.empleadoId}>
                    <td>
                      <div className="font-weight-bold">{det.nombreEmpleado}</div>
                      <small className="text-muted">{det.cargo}</small>
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          det.modalidad === 'SUELDO_BASE'
                            ? 'badge-info'
                            : det.modalidad === 'BASE_MAS_COMISION'
                            ? 'badge-success'
                            : 'badge-warning'
                        }`}
                      >
                        {det.modalidad === 'SUELDO_BASE'
                          ? 'Sueldo Fijo'
                          : det.modalidad === 'BASE_MAS_COMISION'
                          ? 'Base + Comisión'
                          : 'Solo Comisión'}
                      </span>
                    </td>
                    <td className="text-right">{formatLempiras(det.salarioBasePeriodo)}</td>
                    <td className="text-right">
                      {det.modalidad !== 'SUELDO_BASE' ? (
                        <div className="input-group input-group-sm">
                          <div className="input-group-prepend">
                            <span className="input-group-text">L.</span>
                          </div>
                          <input
                            type="number"
                            min="0"
                            step="100"
                            className="form-control text-right font-weight-bold"
                            value={ventasEditadas[det.empleadoId] !== undefined ? ventasEditadas[det.empleadoId] : det.ventasPeriodo}
                            onChange={(e) => handleCambioVenta(det.empleadoId, e.target.value)}
                          />
                        </div>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="text-center font-weight-bold">
                      {det.tasaComision > 0 ? `${det.tasaComision}%` : '—'}
                    </td>
                    <td className="text-right text-success font-weight-bold">
                      {formatLempiras(det.montoComision)}
                    </td>
                    <td className="text-right text-muted">{formatLempiras(det.deduccionIHSS)}</td>
                    <td className="text-right text-muted">{formatLempiras(det.deduccionRAP)}</td>
                    <td className="text-right text-danger">
                      {det.deduccionPrestamo > 0 ? `-${formatLempiras(det.deduccionPrestamo)}` : '—'}
                    </td>
                    <td className="text-right text-danger font-weight-bold">
                      -{formatLempiras(det.totalDeducciones)}
                    </td>
                    <td className="text-right text-primary font-weight-bold" style={{ fontSize: '1rem' }}>
                      {formatLempiras(det.sueldoNeto)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="font-weight-bold" style={{ backgroundColor: 'var(--n8-panel, rgba(255,255,255,0.05))' }}>
                <tr>
                  <td colSpan="2" className="text-uppercase">Totales del Período</td>
                  <td className="text-right">{formatLempiras(calculo?.totalBruto)}</td>
                  <td colSpan="2"></td>
                  <td className="text-right text-success">{formatLempiras(calculo?.totalComisiones)}</td>
                  <td colSpan="3"></td>
                  <td className="text-right text-danger">-{formatLempiras(calculo?.totalDeducciones)}</td>
                  <td className="text-right text-primary" style={{ fontSize: '1.1rem' }}>
                    {formatLempiras(calculo?.totalNeto)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Acciones de Guardado */}
        <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
          <Link to="/app/admin/planillas" className="btn btn-outline-secondary">
            Cancelar
          </Link>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-primary font-weight-bold shadow-sm"
              disabled={procesando}
              onClick={() => handleGuardar(false)}
            >
              <i className="fas fa-save mr-1"></i> Guardar como Borrador
            </button>
            <button
              type="button"
              className="btn btn-primary font-weight-bold shadow-sm"
              disabled={procesando}
              onClick={() => handleGuardar(true)}
            >
              <i className="fas fa-check-circle mr-1"></i> Generar y Aprobar Nómina
            </button>
          </div>
        </div>
      </Card>
    </Page>
  );
};

export default GenerarPlanilla;
